import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackFailure, trackOutcome } from '@/analytics/events';
import { APPOINTMENTS_QUERY_KEY } from '@/services/firestore/appointments';
import {
  GIFT_CARDS_QUERY_KEY,
  getSaleById,
  listGiftCards,
  listRecentSales,
  listSalesForRange,
  saleQueryKey,
  SALES_QUERY_KEY,
} from '@/services/firestore/revenue';
import {
  captureTerminalPayment,
  createRevenueCheckout,
  createRevenuePaymentIntent,
  createRevenueRefund,
  createRevenueSetupIntent,
  voidCheckoutSale,
  createTerminalConnectionToken,
  createTerminalPaymentIntent,
  getTerminalConfiguration,
  recordTerminalPaymentFailure,
  REVENUE_FUNCTIONS_QUERY_KEY,
  updateTerminalPaymentReceiptEmail,
} from '@/services/revenue/functions';

const tenMinutes = 10 * 60 * 1000;

function checkoutSummary(payload: Parameters<typeof createRevenueCheckout>[0]) {
  const lineItemTypes = payload.lineItems.map((item) => item.type);

  return {
    source: payload.source,
    line_item_count: payload.lineItems.length,
    payment_count: payload.payments.length,
    has_service: lineItemTypes.includes('service'),
    has_gift_card_purchase: lineItemTypes.includes('giftCard'),
    has_account_credit_purchase: lineItemTypes.includes('accountCredit'),
    has_gift_card_redemption: payload.payments.some((payment) => payment.type === 'giftCard'),
    has_discount: payload.discounts.length > 0,
    has_tip: payload.tipCents > 0,
    has_client_notes: Boolean(payload.notes),
    total_payment_cents: payload.payments.reduce((total, payment) => total + payment.amountCents, 0),
  };
}

export function useSale(saleId: string | undefined) {
  return useQuery({
    queryKey: saleQueryKey(saleId ?? ''),
    queryFn: () => getSaleById(saleId!),
    staleTime: tenMinutes,
    enabled: Boolean(saleId),
  });
}

export function useRecentSales() {
  return useQuery({
    queryKey: SALES_QUERY_KEY,
    queryFn: () => listRecentSales(),
    staleTime: tenMinutes,
  });
}

export function useSalesForRange(startAt: string, endAt: string) {
  return useQuery({
    queryKey: [...SALES_QUERY_KEY, 'range', startAt, endAt],
    queryFn: () => listSalesForRange(startAt, endAt),
    staleTime: tenMinutes,
    enabled: Boolean(startAt && endAt),
  });
}

export function useGiftCards(resultLimit = 20) {
  return useQuery({
    queryKey: [...GIFT_CARDS_QUERY_KEY, resultLimit],
    queryFn: () => listGiftCards(resultLimit),
    staleTime: tenMinutes,
  });
}

export function useTerminalConfiguration() {
  return useQuery({
    queryKey: [...REVENUE_FUNCTIONS_QUERY_KEY, 'terminalConfiguration'],
    queryFn: () => getTerminalConfiguration(),
    staleTime: 30 * 60 * 1000,
  });
}

export function useRevenueMutations() {
  const queryClient = useQueryClient();

  const invalidateRevenue = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: APPOINTMENTS_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: SALES_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: GIFT_CARDS_QUERY_KEY }),
    ]);
  };

  return {
    checkout: useMutation({
      mutationFn: createRevenueCheckout,
      onSuccess: async (result, payload) => {
        trackOutcome('pos_checkout', 'succeeded', {
          ...checkoutSummary(payload),
          sale_status: result.sale.status,
          total_cents: result.sale.totalCents,
          balance_due_cents: result.sale.balanceDueCents,
        });
        await invalidateRevenue();
      },
      onError: (error, payload) => {
        trackFailure('pos_checkout', error, checkoutSummary(payload));
      },
    }),
    paymentIntent: useMutation({
      mutationFn: createRevenuePaymentIntent,
      onSuccess: async (_result, payload) => {
        trackOutcome('payment_intent_created', 'succeeded', {
          provider: 'stripe',
          purpose: payload.purpose,
          amount_cents: payload.amountCents,
          has_sale: Boolean(payload.saleId),
          has_appointment: Boolean(payload.appointmentId),
          has_client: Boolean(payload.clientId),
          setup_future_usage: Boolean(payload.setupFutureUsage),
        });
        await invalidateRevenue();
      },
      onError: (error, payload) => {
        trackFailure('payment_intent_created', error, {
          purpose: payload.purpose,
          amount_cents: payload.amountCents,
          has_sale: Boolean(payload.saleId),
          has_appointment: Boolean(payload.appointmentId),
          has_client: Boolean(payload.clientId),
          setup_future_usage: Boolean(payload.setupFutureUsage),
        });
      },
    }),
    setupIntent: useMutation({
      mutationFn: createRevenueSetupIntent,
      onSuccess: async () => {
        trackOutcome('setup_intent_created', 'succeeded', {
          provider: 'stripe',
        });
        await invalidateRevenue();
      },
      onError: (error) => {
        trackFailure('setup_intent_created', error, {
        });
      },
    }),
    refund: useMutation({
      mutationFn: createRevenueRefund,
      onSuccess: async (_result, payload) => {
        trackOutcome('refund_created', 'succeeded', {
          amount_cents: payload.amountCents,
          has_reason: Boolean(payload.reason),
        });
        await invalidateRevenue();
      },
      onError: (error, payload) => {
        trackFailure('refund_created', error, {
          amount_cents: payload.amountCents,
          has_reason: Boolean(payload.reason),
        });
      },
    }),
    voidCheckout: useMutation({
      mutationFn: voidCheckoutSale,
      onSuccess: async () => {
        await invalidateRevenue();
      },
    }),
    terminalConnectionToken: useMutation({
      mutationFn: createTerminalConnectionToken,
      onSuccess: (result) => {
        trackOutcome('terminal_connection_token_requested', 'succeeded', {
          provider: 'stripe',
          configured_location: Boolean(result.stripeLocationId),
        });
      },
      onError: (error) => {
        trackFailure('terminal_connection_token_requested', error, {
        });
      },
    }),
    terminalPaymentIntent: useMutation({
      mutationFn: createTerminalPaymentIntent,
      onSuccess: async (result, payload) => {
        trackOutcome('terminal_payment_intent_created', 'succeeded', {
          provider: 'stripe',
          amount_cents: payload.amountCents,
          status: result.status,
          has_reader: Boolean(payload.reader),
          reader_device_type: payload.reader?.deviceType,
        });
        await invalidateRevenue();
      },
      onError: (error, payload) => {
        trackFailure('terminal_payment_intent_created', error, {
          amount_cents: payload.amountCents,
          has_reader: Boolean(payload.reader),
          reader_device_type: payload.reader?.deviceType,
        });
      },
    }),
    terminalCapture: useMutation({
      mutationFn: captureTerminalPayment,
      onSuccess: async (result, payload) => {
        trackOutcome('terminal_payment_captured', 'succeeded', {
          status: result.status,
          sale_status: result.saleStatus,
          balance_due_cents: result.balanceDueCents,
          has_reader: Boolean(payload.reader),
          reader_device_type: payload.reader?.deviceType,
        });
        await invalidateRevenue();
      },
      onError: (error, payload) => {
        trackFailure('terminal_payment_captured', error, {
          has_reader: Boolean(payload.reader),
          reader_device_type: payload.reader?.deviceType,
        });
      },
    }),
    terminalFailure: useMutation({
      mutationFn: recordTerminalPaymentFailure,
      onSuccess: async (result, payload) => {
        trackOutcome('terminal_payment_failed', 'succeeded', {
          recorded_status: result.status,
          sale_status: result.saleStatus,
          has_reason: Boolean(payload.reason),
        });
        await invalidateRevenue();
      },
      onError: (error, payload) => {
        trackFailure('terminal_payment_failed', error, {
          has_reason: Boolean(payload.reason),
        });
      },
    }),
    updateTerminalReceiptEmail: useMutation({
      mutationFn: updateTerminalPaymentReceiptEmail,
    }),
  };
}
